import { useEffect, useState, Key } from 'react'
import { Combobox } from '@headlessui/react'
import { FieldError } from 'react-hook-form'
import classNames from 'classnames'
import { FOCUS_VISIBLE_RING, FOCUS_VISIBLE_RING_OFFSET, INPUT_FOCUS_RING } from '../../../lib/a11yFocus'
import './ISICSelector.css'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks/reduxHooks'
import { getIsicCodes } from '../../../redux/actions/isicActions'
import { selectAllIsic, selectIsicError, selectIsicStatus } from '../../../redux/slices/isicSlice'
import { ISICItem } from '../../../types/ISICItem'
import { FetchStatus } from '../../../types/LoadingStates'
import Fuse from 'fuse.js'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'
import Button from '../Button/Button'
import InlineAlert from '../Alert/InlineAlert'
import ConfirmationModal from '../ConfirmationModal'
import useApi from '../../../hooks/useApi'

//TODO: Needs major refactor
//WARNING: Will cause infinite loops on hot reload due to useEffect losing reference/poor implementation

//TODO: STILL NEEDS A MAJOR REFACTOR!

export interface InterfaceISICSelector {
	updateHandler(selectedCodes: string[]): void
	initialQueries?: string[]
	defaultValue?: string[]
	fixedValues?: string[]
	value?: string[]
	error?: FieldError
	isDirty?: boolean
	min?: number
	max?: number
	darkMode?: boolean
	placeholder?: string
}

// Prettier and ESLint get into a fight about indentation here
// prettier-ignore
export const validate =
	(min?: number, max?: number, extraValues?: string[]) =>
		(value: string[]): string | undefined => {
			extraValues = extraValues || []
			// 'extraValues' allows us to inject fixed values into the validation
			value = value.concat(extraValues)
			console.log('ISIC value', value)

			const minimumValidAmount = min || 1
			const maximumValidAmount = max || 4
			if (value.length < minimumValidAmount) return `Please select at least ${minimumValidAmount} ISIC code${
				minimumValidAmount === 1 ? '' : 's'
			}.`
			if (value.length > maximumValidAmount) return `The maximum amount of selectable ISIC codes is ${maximumValidAmount}.`
			return undefined
		}

export default function ISICSelector({
	updateHandler: onUpdate,
	defaultValue,
	fixedValues = [],
	error,
	isDirty,
	min,
	max,
	value,
	darkMode = false,
	placeholder = 'Search code or description',
}: InterfaceISICSelector): JSX.Element {
	const api = useApi();
	const dispatch = useAppDispatch()
	const isicCodes = useAppSelector(selectAllIsic)
	const isicStatus = useAppSelector(selectIsicStatus)
	const isicError = useAppSelector(selectIsicError)
	const [filteredIsicCodes, setFilteredIsicCodes] = useState<Fuse.FuseResult<ISICItem>[]>([])
	const [selectedIsicCodes, setSelectedIsicCodes] = useState<string[]>(defaultValue || [])
	const [errormsg, setErrorMsg] = useState('')
	const [pendingRemoval, setPendingRemoval] = useState<string | null>(null)

	// Fetch ISIC codes from database
	useEffect(() => {
		if (isicStatus !== FetchStatus.loaded && isicStatus !== FetchStatus.loading) {
			dispatch(getIsicCodes(api))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Fuzzy search library: Fuse.js (https://fusejs.io/)
	// Combined key + extended search so multi-word queries (e.g. "45 sa") match across code/name/description
	// together, rather than requiring all words to appear in a single field.
	const fuse = new Fuse<ISICItem>(isicCodes, {
		keys: [{ name: 'combined', getFn: (item) => `${item.code} ${item.name} ${item.description}` }],
		useExtendedSearch: true,
	})
	const fuzzySearch = (keyword: string) => {
		const query = keyword
			.trim()
			.split(/\s+/)
			.filter((token) => token.length > 0)
			.map((token) => `'${token}`)
			.join(' ')
		if (!query) {
			setFilteredIsicCodes([])
			return
		}
		setFilteredIsicCodes(fuse.search(query))
	}

	//when we are not using form and want to validate directly
	const validateISIC = (min?: number, max?: number) => {
		const combinedCodesTotal = selectedIsicCodes.concat(fixedValues)

		const minval = min || 1
		const maxval = max || 4
		if (combinedCodesTotal?.length + 1 < minval) {
			setErrorMsg(`Please select at least ${minval} ISIC code${minval === 1 ? '' : 's'}.`)
			return errormsg
		} else if (combinedCodesTotal?.length + 1 > maxval) {
			// eslint-disable-next-line no-const-assign
			setErrorMsg(`The maximum amount of selectable ISIC codes is ${maxval}.`)
			return errormsg
		} else {
			setErrorMsg('')
			return undefined
		}
	}

	const showErrors = () => {
		if (error) {
			if (isDirty) {
				return true
			} else {
				return false
			}
		}
		return false
	}

	// Controlled input mode
	useEffect(() => {
		if (
			value &&
			value.length &&
			(value.length !== selectedIsicCodes.length ||
				value.some((code, index) => code !== selectedIsicCodes[index]))
		) {
			setSelectedIsicCodes(value)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value])

	// Any type used as Combobox component expects string setter but is actually receiving fuze result
	const addIsicToSelected = (isicToAdd: any) => {
		// only report genuine user-driven additions to the parent; syncing `value` above must not echo back out,
		// otherwise a parent that feeds the reported value straight back into `value` creates an infinite loop
		const commitAdd = () => {
			const next = [...selectedIsicCodes, isicToAdd.item.code]
			setSelectedIsicCodes(next)
			if (onUpdate) {
				onUpdate(next)
			}
		}
		if (min !== null && max !== null) {
			let validateval = validateISIC(min, max)
			if (validateval === undefined) {
				commitAdd()
			}
		} else {
			if (!validate(1)) {
				return
			}
			commitAdd()
		}
	}
	const removeIsicFromSelected = (isicToRemove: string) => {
		const next = selectedIsicCodes.filter((item) => item !== isicToRemove)
		setSelectedIsicCodes(next)
		if (onUpdate) {
			onUpdate(next)
		}
		validate(-1)
		setErrorMsg('')
	}

	return (
		<>
			{isicStatus === FetchStatus.loading && <LoadingSpinner loadingText='Loading ISIC codes...' />}
			{isicStatus === FetchStatus.error && (
				<InlineAlert type='error'>
					<p>{isicError || 'Could not load ISIC codes. Please try again.'}</p>
					<Button type='Primary' text='Retry' onClick={() => dispatch(getIsicCodes(api))} />
				</InlineAlert>
			)}
			{isicStatus === FetchStatus.loaded && (
				<div>
					{/* `value` is an empty string because we never set this, it’s only unset after a selection */}
					<Combobox as='div' value={''} onChange={addIsicToSelected}>
						<div className='relative mt-1 mb-4'>
							<Combobox.Input
								className={classNames(
									`w-full rounded-md bg-white py-2 pl-3 pr-10 ${INPUT_FOCUS_RING}`,
									{
										'border-error-500 bg-error-050 text-error-800 focus:border-error-500 focus:ring-error-500':
											showErrors(),
										'text-charcoal-800': !showErrors(),
									}
								)}
								placeholder={placeholder}
								onChange={(event) => {
									fuzzySearch(event.target.value)
								}}
							/>
							<Combobox.Button
								as={Button}
								type='White'
								size='sm'
								hasLeadingIcon
								icon='fa-solid fa-magnifying-glass'
								accessibilityLabel='Search ISIC codes'
								additionalClass='absolute top-1/2 transform -translate-y-1/2 right-1 text-charcoal-500'
							/>

							{filteredIsicCodes.length > 0 && (
								<Combobox.Options className='absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'>
									{filteredIsicCodes.map((resultOption) => {
										// Don’t display codes that have already been selected as options or are in the fixed options
										if (
											selectedIsicCodes.includes(resultOption.item.code) ||
											fixedValues?.includes(resultOption.item.code)
										) {
											return null
										}
										const result: ISICItem = isicCodes.find(
											(item) => item.code === resultOption.item.code
										) as ISICItem
										return (
											<Combobox.Option
												key={resultOption.item.id as Key}
												value={resultOption}
												className={({ active }) =>
													classNames(
														'relative cursor-pointer select-none py-2 pl-3 pr-9 text-charcoal-500',
														{ 'bg-algae-100': active }
													)
												}
											>
												{({ active, selected }) => (
													<>
														<div className='flex'>
															<span>
																{resultOption.item.code}
															</span>
															<span className='px-2'>–</span>
															<span className='text-charcoal-500'>{result && result.name}</span>
														</div>

														{selected && (
															<span className='absolute inset-y-0 right-0 flex items-center pr-4'>
																<span
																	className='fa-solid fa-check h-5 w-5'
																	aria-hidden='true'
																></span>
															</span>
														)}
													</>
												)}
											</Combobox.Option>
										)
									})}
								</Combobox.Options>
							)}
						</div>
					</Combobox>
					{showErrors() && error && <p className='mt-2 text-sm text-error-700'>{error.message}</p>}
					{errormsg && (
						<p className={`mt-3 mb-3 text-sm ${darkMode ? 'text-error-400' : 'text-error-700'}`}>
							{errormsg}
						</p>
					)}
					<ul className='my-2'>
						{/* Fixed ISIC codes that can't be dismissed */}
						{fixedValues?.map((fixedValue: string) => {
							const result: ISICItem = isicCodes.find(
								(item) => item.code === fixedValue
							) as ISICItem
							return (
								<li
									key={fixedValue}
									className={classNames('my-1.5 text-sm', { 'text-white': darkMode })}
								>
									<strong>{fixedValue}</strong>
									<span className='px-1'>–</span>
									{result && <span className='flex-auto'>{result.name}</span>}
								</li>
							)
						})}

						{/* Normal selectable and dismissible ISIC codes */}
						{selectedIsicCodes.map((selectedOption: string) => {
							const result: ISICItem = isicCodes.find(
								(item) => item.code === selectedOption
							) as ISICItem
							return (
								<li
									key={selectedOption}
									className={classNames(
										'flex items-center my-1.5 text-sm text-charcoal-100',
										{ 'text-white': darkMode }
									)}
								>
									<strong>{selectedOption}</strong>
									<span className='px-1'>–</span>
									<span className='flex-auto'>{result && result.name}</span>
									<button
										type='button'
										aria-label={`Remove ISIC code ${selectedOption}`}
										className={classNames(
											'ml-2 inline-flex items-center justify-center rounded-sm p-1',
											darkMode ? FOCUS_VISIBLE_RING_OFFSET : FOCUS_VISIBLE_RING
										)}
										onClick={(event) => {
											event.stopPropagation()
											setPendingRemoval(selectedOption)
										}}
									>
										<span className='fa-solid fa-xmark h-4 w-4' aria-hidden='true' />
									</button>
								</li>
							)
						})}
					</ul>
				</div>
			)}
			<ConfirmationModal
				open={!!pendingRemoval}
				onCancel={() => setPendingRemoval(null)}
				onConfirm={() => {
					removeIsicFromSelected(pendingRemoval as string)
					setPendingRemoval(null)
				}}
				title="Remove ISIC code?"
			>
				{ pendingRemoval && (() => {
					const result: ISICItem = isicCodes.find((item) => item.code === pendingRemoval) as ISICItem
					return (
						<p className='mt-8 text-white'>
							Remove {pendingRemoval}{result && ` – ${result.name}`} from this record?
						</p>
					)
				})()}
			</ConfirmationModal>
		</>
	)
}
